import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PlusIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useAppDispatch, RootState } from '../../store';
import {
    fetchLocations,
    createLocation,
    editLocation,
    toggleLocationStatus,
    selectLocationError,
    selectLocationLoading,
    selectedLocations,
    resetForm,
    selectLocationPagination,
} from './locationSlice';
import { Location } from './location';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { usePermission } from '../../components/common/PermissionGuard';
import PageWrapper from '../../components/common/PageWrapper';
import SearchBar from '../../components/searchbar';
import Pagination from '../../components/common/Pagination';
import {formatStatusText, capitalizeWords} from '../../utils/formatters';
import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";
import ChangeStatusConfirmation from "../../components/common/__tests__/ChangeStatusConfirmation";

const Locations: React.FC = () => {
  const dispatch = useAppDispatch();
  const locations = useSelector(selectedLocations);
  const loading = useSelector(selectLocationLoading);
  const error = useSelector(selectLocationError);
  const pagination = useSelector(selectLocationPagination)

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [searchTerm, setSearchTerm] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationStatus, setLocationStatus] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedLocationForStatus, setSelectedLocationForStatus] = useState<Location | null>(null);

  useEffect(() => {
    dispatch(fetchLocations({ page: currentPage, limit: itemsPerPage }));
  }, [dispatch, currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    console.log('Colors state updated:', locations);
  },[locations]);

  // Permissions
  const canCreate = usePermission('locations', 'create');
  const canUpdate = usePermission('locations', 'update');
  const canDelete = usePermission('locations', 'delete');
  const canRead = usePermission('locations', 'read');

  const handleCreateColor = () => {
    dispatch(resetForm());
    setSelectedLocation(null);
    setFormMode('create');
    setLocationName('');
    setLocationStatus(1);
    setIsFormOpen(true);
  };

  const handleEditColor = (location: Location) => {
    setLocationName(location.locationName);
    setLocationStatus(location.status);
    setSelectedLocation(location);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleFormSubmit = () => {
    const formData = { locationName, status:locationStatus };
    console.log('Submitting form with data:', formData);
    console.log('selectedLocation:', selectedLocation);

    if (formMode === 'create') {
      console.log('Creating new location');
      dispatch(createLocation(formData));
    } else if (selectedLocation?.locationId) {
      console.log('editing new location');
      dispatch(editLocation({ id: selectedLocation.locationId, data: formData }));
    }

    setIsFormOpen(false);
    setLocationName('');
    setLocationStatus(1);
    setSelectedLocation(null);
  };

  const toggleStatus = async (location: Location) => {
    const newStatus = location.status === 1 ? 0 : 1;
    try {
      await dispatch(toggleLocationStatus({ 
        id: location.locationId, 
        status: newStatus 
      })).unwrap();
      // Refresh the locations list after successful status update
      dispatch(fetchLocations({ 
        page: currentPage, 
        limit: itemsPerPage, 
        search: searchTerm 
      }));
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  return (
    <PageWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Location Management</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage locations for your application
            </p>
          </div>
          {canCreate && (
            <button
              onClick={handleCreateColor}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Location
            </button>
          )}
        </div>

        {/* Search */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <SearchBar
            placeholder="Search locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-600">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated On</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {locations?.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                        No locations found. {canCreate && 'Create your first location to get started.'}
                      </td>
                    </tr>
                  ) : (
                    locations?.map((location) => (
                        <tr key={location.id}>
                          <td className="px-6 py-4 text-sm font-medium">{location.locationName}</td>
                          <td className="px-6 py-4 text-sm">
                            {(() => {
                              try {
                                const updatedOn = location?.updatedOn;
                                const createdOn = location?.createdOn;
                                
                                // Try to parse and validate dates
                                const updatedDate = updatedOn ? dayjs(updatedOn) : null;
                                const createdDate = createdOn ? dayjs(createdOn) : null;
                                
                                const updatedValid = updatedDate?.isValid();
                                const createdValid = createdDate?.isValid();
                                
                                let displayDate = null;
                                
                                if (updatedValid && createdValid) {
                                  // If both dates are valid, show updated if it's after created, otherwise show created
                                  displayDate = updatedDate.isAfter(createdDate) ? updatedOn : createdOn;
                                } else if (updatedValid) {
                                  displayDate = updatedOn;
                                } else if (createdValid) {
                                  displayDate = createdOn;
                                }
                                
                                if (displayDate) {
                                  const parsed = dayjs(displayDate);
                                  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : 'N/A';
                                }
                                
                                return 'N/A';
                              } catch (error) {
                                console.error('Error formatting date:', error);
                                return 'N/A';
                              }
                            })()}
                          </td>
                          <td className="px-6 py-4 text-sm">{location?.userName || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm cursor-pointer" onClick={() => {
                            setSelectedLocationForStatus(location)
                            setIsStatusModalOpen(true)
                          }}>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                location.status === 1
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {location.status === 1 ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right text-sm">
                            {canUpdate && (
                              <button
                                onClick={() => handleEditColor(location)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Form Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="relative w-96 p-5 border shadow-lg rounded-md bg-white dark:bg-gray-800">
              <button
                onClick={() => setIsFormOpen(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              <h3 className="text-lg font-medium mb-4">
                {formMode === 'create' ? 'Create location' : 'Edit location'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Location Name</label>
                  <input
                    type="text"
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter location name"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 text-sm bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFormSubmit}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md"
                >
                  {formMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {isStatusModalOpen && selectedLocationForStatus && (
        <ChangeStatusConfirmation
          open={isStatusModalOpen}
          onClose={() => {
            setIsStatusModalOpen(false);
            setSelectedLocationForStatus(null);
          }}
          onConfirm={() => {
            toggleStatus(selectedLocationForStatus);
            setIsStatusModalOpen(false);
            setSelectedLocationForStatus(null);
          }}
          title="Change Status"
          message={`Are you sure you want to change status to ${selectedLocationForStatus.status === 1 ? 'Inactive' : 'Active'}?`}
        />
      )}

      {pagination && pagination.total > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => {
            setCurrentPage(page);
            dispatch(fetchLocations({ page, limit: itemsPerPage }));
          }}
          onItemsPerPageChange={(limit) => {
            setItemsPerPage(limit);
            setCurrentPage(1);
            dispatch(fetchLocations({ page: 1, limit }));
          }}
          showItemsPerPage={true}
        />
      )}

    </PageWrapper>
  );
};

export default Locations;
